import { Draggable } from 'react-beautiful-dnd';
import { Card } from '@bancobarigui/barihaus';
import { Group } from '../types';
import { GroupHeader } from './GroupHeader';
import { GroupContent } from './GroupContent';
import { useEditableTitle, useDragAndDrop, useCollapse } from '../shared/hooks';
import { useChecklistContext } from '@/pages/ChecklistNew/contexts/ChecklistContext';

interface GroupItemProps {
  group: Group;
  index: number;
  onEdit: (groupId: string, name: string) => void;
  onRemove: (groupId: string) => void;
  onDuplicate: (groupId: string) => void;
  onAddSection: (groupId: string, insertIndex?: number) => void;
  onEditSection: (groupId: string, sectionId: string, name: string) => void;
  onRemoveSection: (groupId: string, sectionId: string) => void;
  onDuplicateSection: (groupId: string, sectionId: string) => void;
  onAddQuestion: (groupId: string, sectionId: string, insertIndex?: number) => void;
  onEditQuestion: (groupId: string, sectionId: string, questionId: string, updates: any) => void;
  onRemoveQuestion: (groupId: string, sectionId: string, questionId: string) => void;
  onDuplicateQuestion: (groupId: string, sectionId: string, questionId: string) => void;
  allGroups?: Group[];
  draggedType: string | null;
}

export function GroupItem({
  group,
  index,
  onEdit,
  onRemove,
  onDuplicate,
  onAddSection,
  onEditSection,
  onRemoveSection,
  onDuplicateSection,
  onAddQuestion,
  onEditQuestion,
  onRemoveQuestion,
  onDuplicateQuestion,
  allGroups,
  draggedType,
}: GroupItemProps) {
  const { isEditMode, initialGroupIds } = useChecklistContext();

  const { isEditing, editValue, setEditValue, startEditing, saveEdit, cancelEdit } =
    useEditableTitle({
      initialValue: group.name,
      onSave: (value) => onEdit(group.id, value),
    });

  const shouldStartCollapsed = isEditMode && initialGroupIds.has(group.id);
  const { isCollapsed, toggle: toggleCollapse } = useCollapse(shouldStartCollapsed);

  const { isDragOver, handleDragOver, handleDragLeave, handleDrop } = useDragAndDrop({
    onDrop: () => onAddSection(group.id),
    acceptedType: 'new-section',
  });

  return (
    <Draggable draggableId={`group-${group.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`mb-4 ${snapshot.isDragging ? 'opacity-50' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Card
            className={`border-2 ${
              isDragOver
                ? 'border-green-400 bg-green-50'
                : 'border-blue-200'
            }`}
            bodyStyle={{ padding: '12px' }}
          >
            <GroupHeader
              name={group.name}
              isEditing={isEditing}
              editValue={editValue}
              onEditValueChange={setEditValue}
              onSave={saveEdit}
              onCancel={cancelEdit}
              onEdit={startEditing}
              onDuplicate={() => onDuplicate(group.id)}
              onRemove={() => onRemove(group.id)}
              isCollapsed={isCollapsed}
              onToggleCollapse={toggleCollapse}
              dragHandleProps={provided.dragHandleProps || null}
            />

            {!isCollapsed && (
              <GroupContent
                group={group}
                onAddSection={(insertIndex) => onAddSection(group.id, insertIndex)}
                onEditSection={(sectionId, name) => onEditSection(group.id, sectionId, name)}
                onRemoveSection={(sectionId) => onRemoveSection(group.id, sectionId)}
                onDuplicateSection={(sectionId) => onDuplicateSection(group.id, sectionId)}
                onAddQuestion={(sectionId, insertIndex) => onAddQuestion(group.id, sectionId, insertIndex)}
                onEditQuestion={(sectionId, questionId, updates) =>
                  onEditQuestion(group.id, sectionId, questionId, updates)
                }
                onRemoveQuestion={(sectionId, questionId) =>
                  onRemoveQuestion(group.id, sectionId, questionId)
                }
                onDuplicateQuestion={(sectionId, questionId) =>
                  onDuplicateQuestion(group.id, sectionId, questionId)
                }
                allGroups={allGroups || []}
                isDragging={snapshot.isDragging}
                draggedType={draggedType}
              />
            )}
          </Card>
        </div>
      )}
    </Draggable>
  );
}
